import React from 'react';
import { captureError, SEVERITY } from '../../services/errorService';

/**
 * AppErrorBoundary — Top-level React error boundary.
 *
 * - Catches render errors in the subtree
 * - Logs via errorService (CRITICAL severity)
 * - Shows a minimal fallback UI with a "Recharger" button
 * - Intentionally uses inline styles so it renders even if Tailwind fails to load
 */
export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const firstLine = info?.componentStack
      ?.split('\n')
      .find(l => l.trim()) ?? '';
    const entry = captureError(
      error,
      `React render: ${firstLine.trim()}`,
      SEVERITY.CRITICAL,
    );
    this.setState({ errorId: entry.id });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          position: 'fixed', inset: 0,
          background: '#09090b',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '2rem', textAlign: 'center',
          color: '#fff', fontFamily: 'system-ui, -apple-system, sans-serif',
          zIndex: 9999,
        }}
      >
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>😵</div>

        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>
          Quelque chose s'est cassé
        </h1>

        <p style={{
          color: '#a1a1aa', fontSize: '0.875rem',
          marginBottom: '1.5rem', maxWidth: '320px', lineHeight: 1.6,
        }}>
          L'application a rencontré une erreur inattendue.
          {this.state.errorId && (
            <span style={{
              display: 'block', marginTop: '0.5rem',
              fontSize: '0.7rem', opacity: 0.5, fontFamily: 'monospace',
            }}>
              {this.state.errorId}
            </span>
          )}
        </p>

        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 2rem',
            background: '#27272a',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '0.75rem',
            color: '#fff', fontSize: '0.875rem', fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Recharger
        </button>
      </div>
    );
  }
}
